import './project-import-modal.scss';

import { ThunkDispatch } from 'redux-thunk';

import { BootstrapJQ } from '../../models';

import { DragAndDropProcessor } from '../drag-and-drop/drag-and-drop';
import { FancyMessagePaneState, FancyMessagePaneStates } from '../fancy-message-pane/fancy-message-pane';
import { OligoConfigPreferenceChangeHandler, OligoConfigPreferenceOptions, preferenceOptionsToPreference } from '../oligo-config-preference/oligo-config-preference';
import { OligoModalSupportService, OligoModalSupportStatus, OligoModalSupportStatuses, OligoModalTypes } from '../oligo-modal-support/oligo-modal-support';

import { ImportProjectRequest, ProjectService } from '../../service/project-service';

import { hasLocalFileSupport } from '../../models/client';
import * as Model from '../../models';
import * as I18n from '../../models/i18n';
import * as ProjectActions from '../../actions/project-actions';

type ScopeWatcher = () => void;

type ProjectImportTemplateResolvedData = {
  templateData: string[];
  errors: Model.OligoItemError[];
};

export type ProjectImportState = 'processing' | 'success' | 'failed';

export const ProjectImportStates: {
  PROCESSING: ProjectImportState;
  SUCCESS: ProjectImportState;
  FAILED: ProjectImportState;
} = {
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export type ProjectImportModalSupportStatus = 'OK' | 'CANCEL' | 'CLOSE';

export type ProjectImportModalResponse = {
  status: ProjectImportModalSupportStatus;
  projects?: Model.ProjectItem[];
  errors?: Model.OligoItemError[];
};

export type ProjectImportModalActionable = {
  open(): ng.IPromise<ProjectImportModalResponse>;
};

export const ProjectImportModalSupportStatuses: {
  OK: ProjectImportModalSupportStatus;
  CANCEL: ProjectImportModalSupportStatus;
  CLOSE: ProjectImportModalSupportStatus;
} = {
  OK: 'OK',
  CANCEL: 'CANCEL',
  CLOSE: 'CLOSE',
};

const MODAL_ID: string = 'project-import-modal';

const SAMPLE_DATA_TEMPLATE: string = `
<div class='project-import-modal__text-header'>
{{COL_HEADERS}}
</div>
<textarea class='project-import-modal__text-area'>
ACGTACGTGCTACGT,SamplePrimer1,Jane Doe
AGCTGTAACCGGTT,SamplePrimer2,John Doe,50N,,,DSL
AGTTCCGGGCCAAA,SamplePrimer3,Jane Doe,50N,HEX,,HPL
</textarea>
`;

type ErrorAlertType = 'MISSING_REQUIRED_ONLY' | 'INCORRECT_CONFIG_ONLY' | 'MIXED_ERROR';

const ErrorAlertTypes: {
  MISSING_REQUIRED_ONLY: ErrorAlertType;
  INCORRECT_CONFIG_ONLY: ErrorAlertType;
  MIXED_ERROR: ErrorAlertType;
} = {
  MISSING_REQUIRED_ONLY: 'MISSING_REQUIRED_ONLY',
  INCORRECT_CONFIG_ONLY: 'INCORRECT_CONFIG_ONLY',
  MIXED_ERROR: 'MIXED_ERROR',
};

export class ProjectImportModalSupport implements ProjectImportModalActionable {

  public open(): ng.IPromise<ProjectImportModalResponse> {
    throw new Error('Unbound!');
  }

  public listen(ctrl: ProjectImportModalActionable): void {
    this.open = ctrl.open.bind(ctrl);
  }
}

type I18nAware = {
  IMPORT_OLIGO_PROJECT_TITLE: string;
  IMPORT_OLIGO_PROJECT_CANCEL_LABEL: string;
  IMPORT_OLIGO_PROJECT_OK_LABEL: string;
  IMPORT_OLIGO_PROJECT_DOWNLOAD_TEMPLATE_LABEL: string;
  IMPORT_OLIGO_PROJECT_UPLOAD_FILE_LABEL: string;
  IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL: string;
  IMPORT_OLIGO_PROJECT_SHOW_PREFERENCES_LABEL: string;
  IMPORT_OLIGO_PROJECT_HIDE_PREFERENCES_LABEL: string;
  IMPORT_OLIGO_PROJECT_DRAG_AREA_INSTRUCTIONS: string;
  IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS: string;
  IMPORT_OLIGO_PROJECT_OLIGO_COUNT_LABEL: string;
  IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_LABEL: string;
  IMPORT_OLIGO_PROJECT_OLIGO_SAMPLE_DATA_LABEL: string;
  IMPORT_OLIGO_PROJECT_OLIGO_PREFERENCE_HINT_LABEL: string;
  IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT: string;
  IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT: string;
  IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL: string;
  IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL: string;
  IMPORT_OLIGO_PROJECT_TD_EXT_UNSUPPORTED_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT: string;
  IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL: string;
  IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL: string;
  IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL: string;
  IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT: string;
};

type ProjectImportModalProperties = {
  countryCode?: string;
  langCode?: string;
  ready: boolean;
  maxNoOfOligos: number;
  hasExistingProjects: boolean;
  templateColumnDefs: Model.ProjectTemplateColumnDef[];
  supportedFileExtensions: string[];
  sampleDataContent?: string;
  templateURL?: string;
};

type ProjectImportModalOptions = {
  onImportStateChange?: (change: { state: ProjectImportState }) => void;
  templatePath?: string;
  dndProcessor?: DragAndDropProcessor<ProjectImportTemplateResolvedData> | null;
  delegate?: ProjectImportModalSupport;
};

function templateLocation(countryCode?: string, langCode?: string, templatePath?: string): string | undefined {
  if (!countryCode || !langCode || !templatePath) {
    return undefined;
  }
  return (templatePath)
      .replace('{COUNTRY_CODE}', countryCode)
      .replace('{LANG_CODE}', langCode);
}

function templateDataAsTextRowData(templateColumnDefs: Model.ProjectTemplateColumnDef[]): (data: Model.ProjectTemplateData) => string {
  type ParameterDataMap = { [key: string]: Model.Parameter };
  return (data: Model.ProjectTemplateData) => {
    // Sample Format: AGTACGATGGATAGTACGATGGATAGTACGATGGATAGTACGATGGATAGTACGATGGAT,SamplePrimer1,Jane Doe,50N,HEX,PHO,PAG,QLC10
    const parameterDataMap = Object.keys(data).reduce((acc: ParameterDataMap, property: string) => {
      const parameter = data[property] as Model.Parameter;
      acc[parameter.id] = parameter;
      return acc;
    }, {} as ParameterDataMap);
    const rowData = templateColumnDefs.map((columnDef): string => {
      return (parameterDataMap[columnDef.id].value || '') as string;
    });
    const rowText: string = rowData.join(',').replace(/,+$/, '');
    return rowText;
  };
}

function templateTextDataAsFiles(textContent: string, templateColumnDefs: Model.ProjectTemplateColumnDef[], files?: Model.FileItem[] | null): Model.FileItem[] {
  const rowHeader: string = templateColumnDefs.map((columnDef) => columnDef.standardName).join(',');
  const text: string = `${rowHeader}\n${textContent}`;
  let blob: Blob;
  try {
    blob = new Blob([text], { type: 'text/csv'});
  } catch (e) {
    blob = undefined as any as Blob;
  }
  const fileName: string = files && files.length > 0 ? files[0].name : 'paste-content.csv';
  const name: string = `${fileName.substring(0, fileName.lastIndexOf('.'))}.csv`;
  return [{ name, blob, text }];
}

function alertTypeFromErrors(errors: Model.OligoItemError[]): ErrorAlertType {
  const hasMissingRequiredError: boolean = errors.filter((errorItem) => {
    const { id } = errorItem;
    return id === I18n.DictionaryKeys.ERROR_MISSING_REQUIRED_GENERIC_MESSAGE ||
           id === I18n.DictionaryKeys.ERROR_MISSING_REQUIRED_OLIGO_NAME_ONLY ||
           id === I18n.DictionaryKeys.ERROR_MISSING_REQUIRED_OLIGO_RESEARCHER_NAME_ONLY ||
           id === I18n.DictionaryKeys.ERROR_MISSING_REQUIRED_RESEARCHER_NAME_ONLY ||
           id === I18n.DictionaryKeys.ERROR_MISSING_REQUIRED_SEQUENCE_ONLY;
  }).length > 0;
  const hasIncorrectConfigError: boolean = errors.filter((errorItem) => {
    const { id } = errorItem;
    return id === I18n.DictionaryKeys.ERROR_INCORRECT_CONFIGURATION;
  }).length > 0;
  let alertType: ErrorAlertType = ErrorAlertTypes.MIXED_ERROR;
  if (hasMissingRequiredError) {
    alertType = ErrorAlertTypes.MISSING_REQUIRED_ONLY;
  }
  if (hasIncorrectConfigError) {
    alertType = ErrorAlertTypes.INCORRECT_CONFIG_ONLY;
  }
  if (hasMissingRequiredError && hasIncorrectConfigError) {
    alertType = ErrorAlertTypes.MIXED_ERROR;
  }
  return alertType;
}

export class ProjectImportModalController implements ng.IComponentController, I18nAware, ProjectImportModalProperties, ProjectImportModalOptions, ProjectImportModalActionable {

  public MODAL_ID: string = MODAL_ID;
  public MODAL_URL_PATH: string = '/import';
  public IMPORT_OLIGO_PROJECT_TITLE: string = 'Bulk Upload';
  public IMPORT_OLIGO_PROJECT_CANCEL_LABEL: string = 'Cancel';
  public IMPORT_OLIGO_PROJECT_OK_LABEL: string = 'Import';
  public IMPORT_OLIGO_PROJECT_DOWNLOAD_TEMPLATE_LABEL: string = 'Download template';
  public IMPORT_OLIGO_PROJECT_UPLOAD_FILE_LABEL: string = 'Upload file';
  public IMPORT_OLIGO_PROJECT_SHOW_PREFERENCES_LABEL: string = 'Show default preferences';
  public IMPORT_OLIGO_PROJECT_HIDE_PREFERENCES_LABEL: string = 'Hide default preferences';
  public IMPORT_OLIGO_PROJECT_DRAG_AREA_INSTRUCTIONS: string = `<div class='file-supported'>Drag and drop your file</div><div class='file-supported'>OR</div><div>Paste your copied data here</div>`;
  public IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS: string = [
    `For bulk setup, <a href='{{${I18n.PlaceHolderKeys.DOWNLOAD_TEMPLATE_URL}}}' target='_blank'>download</a> sequence template, enter and save the sequence information prior to uploading the file.`,
    'Default preferences will be applied to all blank entries in the tabular form upon saving.<br/>',
    `Supported template file formats for upload are {{${I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS}}}. Information being displayed is comma delimited.`,
  ].join('\n');
  public IMPORT_OLIGO_PROJECT_OLIGO_COUNT_LABEL: string = 'Number of oligos: ';
  public IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_LABEL: string = 'Maximum oligos: ';
  public IMPORT_OLIGO_PROJECT_OLIGO_SAMPLE_DATA_LABEL: string = 'See sample data';
  public IMPORT_OLIGO_PROJECT_OLIGO_PREFERENCE_HINT_LABEL: string = [
    '<div><b>Note:</b> Selected options below will be applied to all oligos where scale, purification, or formulation were not selected.</div>',
    '<div>This will not overwrite any existing spreadsheet selections.</div>',
  ].join('');
  public IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL: string = 'Ok';
  public IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT: string = [
    `You have exceeded the maximum limit of {{${I18n.PlaceHolderKeys.MAX_OLIGO_COUNT}}} oligos.`,
    'Please review your oligo entries before importing them.',
  ].join(' ');
  public IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT: string = 'This will overwrite your existing data. Are you sure you want to continue?';
  public IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL: string = 'Ok';
  public IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL: string = 'Cancel';
  public IMPORT_OLIGO_PROJECT_TD_EXT_UNSUPPORTED_TEXT: string = [
    'User file format is not supported.',
    'Please review your template file.',
    `Accepted file formats: {{${I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS}}}`,
  ].join(' ');
  public IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL: string = 'Import';
  public IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL: string = 'Review';
  public IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT: string = [
    'One or more oligos does not meet the minimum requirements: sequence, sequence name and researcher name.',
    'One or more oligo configurations is not compatible and cannot be applied.',
  ].join('\n');
  public IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT: string = 'One or more oligos does not meet the minimum requirements: sequence, sequence name and researcher name.';
  public IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT: string = 'One or more oligo configurations are not compatible and cannot be applied.';
  public IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT: string = [
    'To review your oligo configurations, click Review.',
    `Otherwise, click 'Import' and oligos without minimum requirements will not be imported and default configurations options will be applied to the problematic oligos.`,
    'You can continue to make changes to your oligos once they are imported into the main design tool.',
  ].join('\n');
  public IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT: string = [
    'To review your oligo configurations, click Review.',
    `Otherwise, click 'Import' and oligos without minimum requirements will not be imported.`,
    'You can continue to make changes to your oligos once they are imported into the main design tool.',
  ].join('\n');
  public IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT: string = [
    'To review your oligo configurations, click Review.',
    `Otherwise, click 'Import' and default configurations options would be applied to the problematic oligos.`,
    'You can continue to make changes to your oligos once they are imported into the main design tool.',
  ].join('\n');
  public IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL: string = 'Problematic oligos:';
  public IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL: string = 'Continue';
  public IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL: string = 'Cancel';
  public IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT: string = 'Bulk import will overwrite existing oligos information in the project.';

  public supportedFileExtensions: string[] = [];
  public busy: boolean = false;
  public templateTextContent: string = '';
  public preferenceChangeHandler: OligoConfigPreferenceChangeHandler;

  public textAreaActive: boolean = false;
  public preferencesShown: boolean = true;
  public preferenceToggleSupported: boolean = false;
  public ready: boolean = false;
  public isOpen: boolean = false;
  public hasExistingProjects: boolean = false;
  public noOfLineItems: number = 0;
  public maxNoOfOligos: number = 200;
  public templateColumnDefs: Model.ProjectTemplateColumnDef[] = [];
  public notificationState: FancyMessagePaneState = FancyMessagePaneStates.INFO;
  public notificationMessages: string[] = [];

  public textAreaRowNumbers: string = '';
  public textAreaNoOfLines: number = 0;

  public importProject: ProjectActions.ProjectImport | null = null;
  public onImportStateChange?: (change: { state: ProjectImportState }) => void;
  public countryCode?: string;
  public langCode?: string;
  public templatePath?: string;
  public templateURL?: string;
  public dndProcessor?: DragAndDropProcessor<ProjectImportTemplateResolvedData> | null;
  public delegate?: ProjectImportModalSupport;
  public initialPreference?: Model.ProjectPreference;
  public preferenceTemmplate?: Model.ProjectPreference;
  public templateParseErrors?: Model.OligoItemError[];
  public sampleDataContent?: string;

  private oligoTemplateDef?: Model.OligoParamDefs;
  private resultDeferred?: ng.IDeferred<ProjectImportModalResponse>;
  private files?: Model.FileItem[] | null;
  private preference?: Model.ProjectPreference;
  private jq?: BootstrapJQ;
  private inputJQ?: JQLite;

  constructor(private $scope: ng.IScope,
              private $timeout: ng.ITimeoutService,
              private $q: ng.IQService,
              private $location: ng.ILocationService,
              $ngRedux: Model.INgRedux,
              projectActions: ProjectActions.ProjectActions,
              private projectService: ProjectService,
              private oligoModalSupport: OligoModalSupportService) {
    const bindDispatchers = (dispatch: ThunkDispatch<Model.RootState, object, Model.ReduxAction<object>>) => {
      this.importProject = (request: ProjectActions.ProjectImportFnRequest) => {
        return dispatch(projectActions.importProject(request));
      };
    };
    const unsubscribeRedux: ScopeWatcher = $ngRedux.connect(this.mapStateToThis.bind(this), bindDispatchers)(this);
    const unwatchDelegate = $scope.$watch(() => this.delegate, (delegate?: ProjectImportModalSupport) => {
      if (!delegate) {
        return;
      }
      delegate.listen(this);
    });
    const unwatchTemplateText = $scope.$watch(() => this.templateTextContent, (newContent) => {
      newContent = newContent || '';
      newContent = newContent.replace(/^\s*[\r\n]/gm, '');
      const lineNumbers: string[] = [];
      const noOfNewLines: number = newContent.split('\n').length;
      for (let newLineNo: number = 0; newLineNo < noOfNewLines; newLineNo++) {
        lineNumbers.push(`${newLineNo + 1}`);
      }
      this.textAreaNoOfLines = noOfNewLines;
      this.textAreaRowNumbers = lineNumbers.join('\n');
      const trimmedContent = newContent.trim();
      const noOfLineItems = trimmedContent.length > 0 ?  trimmedContent.split('\n').length : 0;
      this.noOfLineItems = noOfLineItems;
      if (trimmedContent.indexOf('\t') < 0) {
        return;
      }
      this.templateTextContent = trimmedContent.replace(/\t/g, ',');
    });
    const unwatchTemplatePath = $scope.$watch(() => this.templatePath, (templatePath) => {
      const { countryCode, langCode, templateURL } = this;
      if (templateURL) {
        unwatchTemplatePath();
        return;
      }
      this.templateURL = templateLocation(countryCode, langCode, templatePath);
    });
    const unwatchNoOfItems = $scope.$watch(() => this.noOfLineItems, () => {
      let currentNotificationState = this.notificationState;
      let currentNotifications = (this.notificationMessages || []).filter((message) =>
          message !== this.IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT);
      if (this.isExceedingItemLimit()) {
        currentNotifications = [...currentNotifications, this.IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT];
        currentNotificationState = FancyMessagePaneStates.WARN;
      } else {
        currentNotificationState = currentNotifications.length > 0 ? FancyMessagePaneStates.WARN : FancyMessagePaneStates.INFO;
      }
      this.notificationMessages = currentNotifications;
      this.notificationState = currentNotificationState;
    });
    const openWhenReady = () => {
      if (this.ready) {
        this.open();
      } else {
        $timeout(openWhenReady, 500);
      }
    };
    $scope.$on('$locationChangeSuccess', ($event: ng.IAngularEvent, newUrl: string) => {
      const pathRegex: RegExp = new RegExp(`${this.MODAL_URL_PATH}$`);
      const isThisPage: boolean = pathRegex.test(newUrl);
      if (isThisPage) {
        openWhenReady();
      } else if (this.isOpen) {
        this.close();
      }
    });
    $scope.$on('$destroy', () => {
      unsubscribeRedux();
      unwatchDelegate();
      unwatchTemplateText();
      unwatchTemplatePath();
      unwatchNoOfItems();
      this.dndProcessor = null;
    });

    const processFiles = this.processFiles.bind(this);
    const onFileProcessingSuccess = this.onFileProcessingSuccess.bind(this);
    const onFileProcessingFail = this.onFileProcessingFail.bind(this);
    const dndProcessor: DragAndDropProcessor<ProjectImportTemplateResolvedData> = {
      processDroppedFiles: processFiles,
      onDragAndDropSuccess: onFileProcessingSuccess,
      onDragAndDropFailure: onFileProcessingFail,
    };
    const preferenceChangeHandler: OligoConfigPreferenceChangeHandler = {
      onOptionChangeStart: (): void => {
        this.busy = true;
      },
      onOptionChangeSuccess: (newOptions: OligoConfigPreferenceOptions): void => {
        this.preference = preferenceOptionsToPreference(newOptions);
        this.busy = false;
      },
      onOptionChangeFailed: (error: object): void => {
        this.busy = false;
      },
    };
    this.dndProcessor = dndProcessor;
    this.preferenceChangeHandler = preferenceChangeHandler;
    this.processFiles = processFiles;
    this.onFileProcessingSuccess = onFileProcessingSuccess;
    this.onFileProcessingFail = onFileProcessingFail;
    this.topLevelInstructions = this.topLevelInstructions.bind(this);
  }

  public onTextAreaBlur(): void {
    this.textAreaActive = this.templateTextContent.trim().length > 0;
  }

  public filesChanged(inputJQ: JQLite, files?: FileList): void {
    this.inputJQ = inputJQ;
    const clearInputJQ = () => inputJQ.val('');
    if (files) {
      if (files.length > 0) {
        this.processFiles(files)
          .then(this.onFileProcessingSuccess, this.onFileProcessingFail)
          .finally(clearInputJQ);
      }
    } else {
      this.processFilePath(inputJQ.val() as string)
        .then(this.onFileProcessingSuccess, this.onFileProcessingFail)
        .finally(clearInputJQ);
    }

  }

  public processFiles(fileList: FileList): ng.IPromise<ProjectImportTemplateResolvedData> {
    const { templateColumnDefs, langCode } = this;
    const files: Model.FileItem[] = Array.prototype.slice.call(fileList)
      .map((blob: File): Model.FileItem => {
        const { name } = blob;
        return { name, blob };
      });
    this.busy = true;
    this.files = files;
    const asTextRowData = templateDataAsTextRowData(templateColumnDefs);
    return this.projectService.importProjectTemplateData({ files, langCode })
      .then((result) => {
        const { data, errors } = result;
        const templateData: string[] = data.map(asTextRowData);
        return { templateData, errors };
      });
  }

  public processFilePath(path: string): ng.IPromise<ProjectImportTemplateResolvedData> {
    const { templateColumnDefs, langCode } = this;
    const files: Model.FileItem[] = [path]
      .map((name: string): Model.FileItem => {
        const blob: Blob = undefined as any as Blob;
        return { name, blob };
      });
    this.busy = true;
    this.files = files;
    const asTextRowData = templateDataAsTextRowData(templateColumnDefs);
    return this.projectService.importProjectTemplateData({ files, langCode })
      .then((result) => {
        const { data, errors } = result;
        const templateData: string[] = data.map(asTextRowData);
        return { templateData, errors };
      });
  }

  public onFileProcessingSuccess(data: ProjectImportTemplateResolvedData): ng.IPromise<void> {
    const { noOfLineItems, IMPORT_OLIGO_PROJECT_TITLE, IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT } = this;
    const { IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL, IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL } = this;
    const { errors, templateData } = data;
    this.busy = false;
    const hasCurrentData: boolean = noOfLineItems > 0;
    const promptWithExistingDataPromise: ng.IPromise<boolean> = (() => {
      if (hasCurrentData) {
        return this.oligoModalSupport.show({
          title: IMPORT_OLIGO_PROJECT_TITLE,
          type: OligoModalTypes.CONFIRM,
          alertData: {
            state: FancyMessagePaneStates.WARN,
            content: IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT,
          },
          content: '',
          okLabel: IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL,
          cancelLabel: IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL,
        }).then((status) => {
          return status === OligoModalSupportStatuses.OK;
        });
      } else {
        return this.$q.resolve(true);
      }
    })();
    return promptWithExistingDataPromise.then((continueImport: boolean) => {
      if (!continueImport) {
        return;
      }
      this.textAreaActive = templateData.length > 0;
      this.templateTextContent = templateData.length > 0 ? templateData.join('\n') : '';
      this.templateParseErrors = errors;
      this.noOfLineItems = templateData.length;
      this.notificationState = errors && errors.length > 0 ? FancyMessagePaneStates.WARN : FancyMessagePaneStates.INFO;
      this.notificationMessages = errors.map((templateError) => templateError.message);
    });
  }

  public onFileProcessingFail(error: object | object[]): ng.IPromise<void> {
    const { IMPORT_OLIGO_PROJECT_TITLE, IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL } = this;
    this.busy = false;
    let errorMessages: string[] = [];
    const isAppErrorObject = (targetError: object): boolean => {
      const oligoError = targetError as Model.OligoItemError;
      return oligoError &&
             typeof oligoError.id === 'string' &&
             typeof oligoError.message === 'string';
    };
    if (error instanceof Array) {
      errorMessages = error.filter(isAppErrorObject).map((appError) =>
        (appError as Model.OligoItemError).message);
    } else {
      if (isAppErrorObject(error)) {
        errorMessages.push((error as Model.OligoItemError).message);
      }
    }
    if (errorMessages.length > 0) {
      return this.oligoModalSupport.show({
        title: IMPORT_OLIGO_PROJECT_TITLE,
        content: '',
        type: OligoModalTypes.INFO,
        okLabel: IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL,
        alertData: {
          state: FancyMessagePaneStates.WARN,
          content: errorMessages.join('\n'),
        },
      }).then(() => {
        return;
      });
    } else {
      return this.$q.reject(error);
    }
  }

  public noLocalFileSupportProcessFrame(frameJQ: JQLite): void {
    const { templateColumnDefs, langCode } = this;
    const asTextRowData = templateDataAsTextRowData(templateColumnDefs);
    const responseData: string = frameJQ.contents().find('body').text();
    this.projectService.processProjectTemplateData(JSON.parse(responseData), langCode as string)
      .then((result) => {
        const { data, errors } = result;
        const templateData: string[] = data.map(asTextRowData);
        return { templateData, errors };
      }).then(this.onFileProcessingSuccess, this.onFileProcessingFail);
  }

  public clearNotifications(): void {
    this.notificationState = FancyMessagePaneStates.INFO;
    this.notificationMessages = [];
  }

  public isExceedingItemLimit(): boolean {
    return this.noOfLineItems >  this.maxNoOfOligos;
  }

  public isContentValid(): boolean {
    const { templateTextContent } = this;
    const templateContentValid: boolean = templateTextContent ? templateTextContent.trim().length > 0 : false;
    const doesNotExceedOligoLimit: boolean = !this.isExceedingItemLimit();
    return templateContentValid && doesNotExceedOligoLimit;
  }

  public togglePreferences(): void {
    this.preferencesShown = !this.preferencesShown;
  }

  public topLevelInstructions(): string {
    const { IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS, supportedFileExtensions, templateURL } = this;
    return I18n.replacePlaceHolders(IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS, {
      [I18n.PlaceHolderKeys.DOWNLOAD_TEMPLATE_URL]: templateURL || '',
      [I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS]: supportedFileExtensions.join(', ').replace(/,\s([^,]+)$/, ' and $1'),
    });
  }

  public activateTextArea(): void {
    // Do nothing initially. Get's bound on link
  }

  public openFileBrowser(): void {
    // Do nothing initially. Get's bound on link
  }

  public open(): ng.IPromise<ProjectImportModalResponse> {
    const deferred: ng.IDeferred<ProjectImportModalResponse> = this.$q.defer();
    this.showModal();
    this.resultDeferred = deferred;
    this.$scope.$emit(Model.ScopeEvents.BULK_UPLOAD_SHOW);
    // initialize the default preference if this
    // component has never been loaded before by
    // using a new oligo as a preference template
    if (!this.oligoTemplateDef) {
      this.busy = true;
      this.projectService.newOligoItem({ id: '0', index: 0})
        .then((templateOligo: Model.OligoItem) => {
          const { definition } = templateOligo;
          const { basic } = definition;
          const { scale, purification, format } = basic;
          const preference: Model.ProjectPreference = {
            scale: {
              id: scale.selected.id,
              value: scale.selected.value,
            },
            purification: {
              id: purification.selected.id,
              value: purification.selected.value,
            },
            format: {
              id: format.selected.id,
              value: format.selected.value,
            },
            isDefault: true,
          };
          this.initialPreference = { ...preference };
          this.preferenceTemmplate = { ...preference };
          this.oligoTemplateDef = definition;
        }).finally(() => {
          this.busy = false;
        });
    }
    return deferred.promise;
  }

  public ok(): void {
    const { importProject, preference, oligoTemplateDef, files, templateColumnDefs, templateTextContent } = this;
    if (!importProject || !preference || !oligoTemplateDef || !templateTextContent || !templateColumnDefs) {
      return;
    }
    if (templateTextContent.length <= 0) {
      return;
    }
    const request: ImportProjectRequest = {
      files: templateTextDataAsFiles(templateTextContent, templateColumnDefs, files),
      preference: {
        ...preference,
        delivery: {
          id: oligoTemplateDef.basic.delivery.selected.id,
          value: oligoTemplateDef.basic.delivery.selected.value,
        },
      },
    };
    this.busy = true;
    this.projectService.importProject(request).then((response) => {
        const { projects, errors: importErrors } = response;
        const hasErrors: boolean = importErrors ? importErrors.length > 0 : false;
        if (!hasErrors) {
          const successfulImportResponse: ProjectImportModalResponse = {
            projects,
            status: ProjectImportModalSupportStatuses.OK,
          };
          return successfulImportResponse;
        } else {
          this.busy = false;
          return this.openConfirmProjectImport(importErrors).then((confirmImportStatus: OligoModalSupportStatus) => {
            if (confirmImportStatus === OligoModalSupportStatuses.OK) {
              this.busy = true;
              return this.projectService.importProject({ ...request, fixErrorsUsingPreference: true })
                .then((confirmedImportResponse) => {
                  const { projects: confirmedProjects } = confirmedImportResponse;
                  const confirmedProjectResponse: ProjectImportModalResponse = {
                    status: ProjectImportModalSupportStatuses.OK,
                    projects: confirmedProjects,
                  };
                  return confirmedProjectResponse;
                });
            } else {
              const reviewProjectResponse: ProjectImportModalResponse = {
                status: ProjectImportModalSupportStatuses.CANCEL,
                errors: importErrors,
              };
              return reviewProjectResponse;
            }
          });
        }
      }).then((response: ProjectImportModalResponse) => {
        const { status, projects: projectsToImport, errors: projectImportErrors } = response;
        const errorsToShow: Model.OligoItemError[] = projectImportErrors || [];
        this.notificationState = errorsToShow.length > 0 ? FancyMessagePaneStates.WARN : FancyMessagePaneStates.INFO;
        this.notificationMessages = errorsToShow.map((projectImportError) => `<div>${projectImportError.message}</div>`);
        if (status !== ProjectImportModalSupportStatuses.OK) {
          return;
        }
        return this.openConfirmProjectOverwrite().then((continueImportStatus) => {
          if (continueImportStatus === OligoModalSupportStatuses.OK) {
            this.busy = true;
            const projects: Model.ProjectItem[] = projectsToImport as Model.ProjectItem[];
            return importProject({ projects }).then(() => {
              this.busy = false;
              this.resetLocalState();
              if (this.inputJQ) {
                this.inputJQ.val('');
              }
              this.closeModal();
              if (this.resultDeferred) {
                this.resultDeferred.resolve(response);
              }
              this.$scope.$emit(Model.ScopeEvents.BULK_UPLOAD_IMPORT);
            });
          } else {
            return;
          }
        });
    }).finally(() => {
      this.busy = false;
    });
  }

  public cancel(): void {
    this.closeModal();
    this.resetLocalState();
    if (this.resultDeferred) {
      this.resultDeferred.resolve({
        status: ProjectImportModalSupportStatuses.CANCEL,
      });
    }
  }

  public close(): void {
    this.cancel();
  }

  private getJQ(): BootstrapJQ {
    if (!this.jq) {
      this.jq = $(`#${MODAL_ID}`) as BootstrapJQ;
    }
    return this.jq;
  }

  private hideModal(): void {
    this.getJQ().modal('hide');
    this.isOpen = false;
  }

  private showModal(): void {
    this.getJQ().modal({
      backdrop: 'static',
      show: true,
      keyboard: false,
    });
    this.isOpen = true;
  }

  private closeModal(): void {
    this.hideModal();
    this.$location.path('/');
  }

  private openConfirmProjectImport(errors: Model.OligoItemError[]): ng.IPromise<OligoModalSupportStatus> {
    const { IMPORT_OLIGO_PROJECT_TITLE, IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL } = this;
    const { IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL, IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL } = this;
    const { IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT, IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT } = this;
    const { IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT, IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT } = this;
    const { IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT, IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT } = this;
    const alertType: ErrorAlertType = alertTypeFromErrors(errors);
    let alertContent: string = IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT;
    let instructionContent: string = IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT;
    switch (alertType) {
      case ErrorAlertTypes.MISSING_REQUIRED_ONLY:
        alertContent = IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT;
        instructionContent = IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT;
        break;
      case ErrorAlertTypes.INCORRECT_CONFIG_ONLY:
        alertContent = IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT;
        instructionContent = IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT;
        break;
      case ErrorAlertTypes.MIXED_ERROR:
      default:
        alertContent = IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT;
        instructionContent = IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT;
        break;
    }
    const errorContent: string = errors.map((errorItem: Model.OligoItemError) => {
      const { message } = errorItem;
      return `<li>${message}</li>`;
    }).join('');
    return this.oligoModalSupport.show({
      title: IMPORT_OLIGO_PROJECT_TITLE,
      content: `
      <div class="project-import-modal__confirm-instruction">
      ${instructionContent}
      </div>
      <div class="project-import-modal__confirm-error-container">
        <div class="project-import-modal__confirm-error-label">
        ${IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL}
        </div>
        <ul class="project-import-modal__confirm-error-content">${errorContent}</ul>
      </div>
      `,
      type: OligoModalTypes.CONFIRM,
      okLabel: IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL,
      cancelLabel: IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL,
      alertData: {
        state: FancyMessagePaneStates.WARN,
        content: alertContent,
      },
    });
  }

  private openConfirmProjectOverwrite(): ng.IPromise<OligoModalSupportStatus> {
    const { IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL, IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL } = this;
    const { IMPORT_OLIGO_PROJECT_TITLE, IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT, hasExistingProjects } = this;
    return this.$q((resolve: ng.IQResolveReject<OligoModalSupportStatus>,
                    reject: ng.IQResolveReject<object>) => {
      if (hasExistingProjects) {
        // why the timeout? we have to let any previous
        // support modal to close first before showing
        // another one
        this.$timeout(() => {
          this.oligoModalSupport.show({
            title: IMPORT_OLIGO_PROJECT_TITLE,
            content: '',
            type: OligoModalTypes.CONFIRM,
            okLabel: IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL,
            cancelLabel: IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL,
            alertData: {
              state: FancyMessagePaneStates.WARN,
              content: IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT,
            },
          }).then(resolve, reject);
        }, 500);
      } else {
        return resolve(OligoModalSupportStatuses.OK);
      }
    });
  }

  private resetLocalState(): void {
    this.files = null;
    this.templateTextContent = '';
    this.textAreaActive = false;
    this.preferencesShown = true;
    this.noOfLineItems = 0;
    this.initialPreference = this.preferenceTemmplate ? { ...this.preferenceTemmplate } : undefined;
    this.templateParseErrors = [];
    this.clearNotifications();
  }

  private createMaxOligoLimitReachedText(vocabulary: any, maxNoOfOligos: number): string {
    const text: string = vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OLIGO_COUNT_MAX_REACHED_TEXT] || this.IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT;
    return I18n.replacePlaceHolders(text, { [I18n.PlaceHolderKeys.MAX_OLIGO_COUNT]: `${maxNoOfOligos}` });
  }

  private createUnsupportedFileText(vocabulary: any, supportedFileExtensions: string[]): string {
    const text: string = vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EXTENSION_UNSUPPORTED_TEXT] || this.IMPORT_OLIGO_PROJECT_TD_EXT_UNSUPPORTED_TEXT;
    return I18n.replacePlaceHolders(text, {
      [I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS]: supportedFileExtensions.join(', ').replace(/,\s([^,]+)$/, ' and $1'),
    });
  }

  private createSampleDataContent(templateColumnDefs: any[]): string {
    if (templateColumnDefs.length > 0) {
      const sampleDataColumnHeaders: string = templateColumnDefs.map((columnDef) => columnDef.name).join(',');
      return SAMPLE_DATA_TEMPLATE.replace('{{COL_HEADERS}}', sampleDataColumnHeaders);
    }
    return '';
  }

  private getVocabularyOrDefault(vocabulary: any, key: string, defaultValue: string): string {
    return vocabulary[key] || defaultValue;
  }

  private mapStateToThis(state: Model.RootState): I18nAware & ProjectImportModalProperties {
    const { templatePath } = this;
    let { ready, sampleDataContent } = this;
    const { world, projects } = state;
    const { countryCode, language: langCode, vocabulary, maxNoOfOligos, templateColumnDefs: projectTemplateColumnDefs, templateSupportedExtensions: supportedFileExtensions } = world;
    if (!ready) {
      ready = Object.keys(vocabulary).length > 0;
    }
    // needed in final return object
    const hasExistingProjects: boolean = projects && projects.length > 0;
    const templateColumnDefs = [...projectTemplateColumnDefs].sort((a, b) => a.index - b.index);
    const templateURL = templateLocation(countryCode, langCode, templatePath);

    // separate function to find max oligo limit reached
    const maxOligoLimitReachedText = this.createMaxOligoLimitReachedText(vocabulary, maxNoOfOligos);

    // separate function to find unsupported file extension
    const unsupportedFileText = this.createUnsupportedFileText(vocabulary, supportedFileExtensions);

    // separate function to find sample data content
    sampleDataContent = this.createSampleDataContent(templateColumnDefs);

    return {
      ready,
      countryCode,
      langCode,
      maxNoOfOligos,
      hasExistingProjects,
      templateColumnDefs,
      templateURL,
      sampleDataContent,
      supportedFileExtensions,
      IMPORT_OLIGO_PROJECT_TITLE: this.getVocabularyOrDefault(vocabulary, I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TITLE, this.IMPORT_OLIGO_PROJECT_TITLE),
      IMPORT_OLIGO_PROJECT_CANCEL_LABEL: this.getVocabularyOrDefault(vocabulary, I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CANCEL_BUTTON, this.IMPORT_OLIGO_PROJECT_CANCEL_LABEL),
      IMPORT_OLIGO_PROJECT_OK_LABEL: this.getVocabularyOrDefault(vocabulary, I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OK_BUTTON, this.IMPORT_OLIGO_PROJECT_OK_LABEL),
      IMPORT_OLIGO_PROJECT_DOWNLOAD_TEMPLATE_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                    I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_DOWNLOAD_TEMPLATE_LABEL, this.IMPORT_OLIGO_PROJECT_DOWNLOAD_TEMPLATE_LABEL),
      IMPORT_OLIGO_PROJECT_UPLOAD_FILE_LABEL: this.getVocabularyOrDefault(vocabulary,
                                              I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_UPLOAD_FILE_LABEL, this.IMPORT_OLIGO_PROJECT_UPLOAD_FILE_LABEL),
      IMPORT_OLIGO_PROJECT_SHOW_PREFERENCES_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                   I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_SHOW_PREFERENCES_LABEL, this.IMPORT_OLIGO_PROJECT_SHOW_PREFERENCES_LABEL),
      IMPORT_OLIGO_PROJECT_HIDE_PREFERENCES_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                   I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_HIDE_PREFERENCES_LABEL, this.IMPORT_OLIGO_PROJECT_HIDE_PREFERENCES_LABEL),
      IMPORT_OLIGO_PROJECT_DRAG_AREA_INSTRUCTIONS: this.getVocabularyOrDefault(vocabulary,
                                                   I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_DRAG_AREA_INSTRUCTIONS, this.IMPORT_OLIGO_PROJECT_DRAG_AREA_INSTRUCTIONS),
      IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS: this.getVocabularyOrDefault(vocabulary,
                                                   I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TOP_LEVEL_INSTRUCTIONS, this.IMPORT_OLIGO_PROJECT_TOP_LEVEL_INSTRUCTIONS),
      IMPORT_OLIGO_PROJECT_OLIGO_COUNT_LABEL: this.getVocabularyOrDefault(vocabulary,
                                              I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OLIGO_COUNT_LABEL, this.IMPORT_OLIGO_PROJECT_OLIGO_COUNT_LABEL),
      IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                  I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OLIGO_COUNT_MAX_LABEL, this.IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_LABEL),
      IMPORT_OLIGO_PROJECT_OLIGO_SAMPLE_DATA_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                    I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OLIGO_SAMPLE_DATA_LABEL, this.IMPORT_OLIGO_PROJECT_OLIGO_SAMPLE_DATA_LABEL),
      IMPORT_OLIGO_PROJECT_OLIGO_PREFERENCE_HINT_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                        I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_OLIGO_PREFERENCE_HINT_LABEL, this.IMPORT_OLIGO_PROJECT_OLIGO_PREFERENCE_HINT_LABEL),
      IMPORT_OLIGO_PROJECT_OLIGO_COUNT_MAX_TEXT: maxOligoLimitReachedText,
      IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_ERROR_ACCEPT_LABEL, this.IMPORT_OLIGO_PROJECT_ERROR_ACCEPT_LABEL),
      IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT: this.getVocabularyOrDefault(vocabulary,
                                              I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_DATA_OVERWRITE_TEXT, this.IMPORT_OLIGO_PROJECT_TD_OVERWRITE_TEXT),
      IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                  I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_DATA_OVERWRITE_OK_LABEL, this.IMPORT_OLIGO_PROJECT_TD_OVERWRITE_OK_LABEL),
      IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                      I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_DATA_OVERWRITE_CANCEL_LABEL, this.IMPORT_OLIGO_PROJECT_TD_OVERWRITE_CANCEL_LABEL),
      IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL: this.getVocabularyOrDefault(vocabulary,
                                             I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_OK_LABEL, this.IMPORT_OLIGO_PROJECT_CONFIRM_OK_LABEL),
      IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                 I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_CANCEL_LABEL, this.IMPORT_OLIGO_PROJECT_CONFIRM_CANCEL_LABEL),
      IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT: this.getVocabularyOrDefault(vocabulary,
                                               I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_ALERT_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT: this.getVocabularyOrDefault(vocabulary,
      I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_ALERT_CONFIG_INCORRECT_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_CONFIG_INCORRECT_ONLY_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT: this.getVocabularyOrDefault(vocabulary,
      I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_ALERT_MISSING_REQUIRED_ONLY_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT: this.getVocabularyOrDefault(vocabulary,
                                                     I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_INSTRUCTION_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT: this.getVocabularyOrDefault(vocabulary,
      I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_CONFIG_INCORRECT_ONLY_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT: this.getVocabularyOrDefault(vocabulary,
      I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT, this.IMPORT_OLIGO_PROJECT_CONFIRM_INSTRUCTION_MISSING_REQUIRED_ONLY_TEXT),
      IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                       I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_CONFIRM_ERROR_REGION_LABEL, this.IMPORT_OLIGO_PROJECT_CONFIRM_ERROR_REGION_LABEL),
      IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                    I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_DATA_OVERWRITE_OK_LABEL, this.IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_OK_LABEL),
      IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL: this.getVocabularyOrDefault(vocabulary,
                                                         I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_DATA_OVERWRITE_CANCEL_LABEL, this.IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_CANCEL_LABEL),
      IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT: this.getVocabularyOrDefault(vocabulary,
                                                I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_DATA_OVERWRITE_TEXT, this.IMPORT_OLIGO_PROJECT_DATA_OVERWRITE_TEXT),
      IMPORT_OLIGO_PROJECT_TD_EXT_UNSUPPORTED_TEXT: unsupportedFileText,
    };
  }
}

export const PROJECT_IMPORT_MODAL_NAME: string = 'projectImportModal';
export const ProjectImportModal: ng.IDirectiveFactory = ($timeout: ng.ITimeoutService, $document: ng.IDocumentService) => {
  return {
    template: (() => {
      const bsJQ: BootstrapJQ = $.fn as BootstrapJQ;
      if (!bsJQ.modal || !bsJQ.modal.Constructor) {
        return '';
      }
      const version: string = bsJQ.modal.Constructor.VERSION;
      if (version) {
        if (version.indexOf('4') === 0) {
          return require('./project-import-modal.tpl.bs4.html');
        }
      } else {
        const isBS2: boolean = bsJQ.modal.toString().indexOf('bs.modal') === -1;
        if (isBS2) {
          return require('./project-import-modal.tpl.html');
        }
      }
      return '';
    })(),
    controller: ProjectImportModalController,
    controllerAs: 'projectImportModal',
    restrict: 'E',
    scope: {
      onImportStateChange: '&',
      templatePath: '<',
      delegate: '<',
    },
    bindToController: true,
    link: ($scope: ng.IScope, $elem: JQLite, attrs: ng.IAttributes, ctrl?: ng.IController) => {
      const controller: ProjectImportModalController = ctrl as ProjectImportModalController;
      let fileBrowserJQ: JQuery<HTMLElement>;
      let sampleDataPopoverJQ: Model.BootstrapJQ | null;
      controller.openFileBrowser = (): void => {
        if (!fileBrowserJQ) {
          if (hasLocalFileSupport()) {
            fileBrowserJQ = $elem.find('#file-open-os');
          } else {
            fileBrowserJQ = $document.find('#no-file-support-template-data-file');
            fileBrowserJQ.on('change', () => {
              controller.filesChanged(fileBrowserJQ);
            });
          }
        }
        fileBrowserJQ.click();
      };
      controller.activateTextArea = (): void => {
        controller.textAreaActive = true;
        $timeout(() => {
          $elem.find('.project-import-modal__text-area').eq(0).focus();
        });
      };
      $timeout(() => {
        const sampleDataPopover: Model.BootstrapJQ = $elem.find('.project-import-modal__item-info--popover') as Model.BootstrapJQ;
        if (sampleDataPopover.popover) {
          const popover: Model.BootstrapPopoverOptions = {
            html: true,
            placement: 'bottom',
            trigger: 'hover',
          };
          sampleDataPopover.popover(popover);
          sampleDataPopoverJQ = sampleDataPopover;
        }
      });

      let pasteArea: JQuery<HTMLElement> | null;
      let rowHeader: JQuery<HTMLElement> | null;
      let pasteAreaScrollWatcher: (() => void) | null;
      const cleanupTextAreaScrollWatcher = () => {
        if (pasteArea && pasteAreaScrollWatcher) {
          pasteArea.off('scroll', pasteAreaScrollWatcher);
        }
        pasteArea = null;
        rowHeader = null;
        pasteAreaScrollWatcher = null;
      };
      const unwatchTextAreaActive = $scope.$watch(() => controller.textAreaActive, (active: boolean) => {
        if (active) {
          $timeout(() => {
            const foundPasteArea = $elem.find('.project-import-modal__text-area');
            const foundRowHeader = $elem.find('.project-import-modal__text-area--rowno');
            const scrollWatcher = () => foundRowHeader.scrollTop(foundPasteArea.scrollTop() || 0);
            foundPasteArea.on('scroll', scrollWatcher);
            pasteArea = foundPasteArea;
            rowHeader = foundRowHeader;
            pasteAreaScrollWatcher = scrollWatcher;
          });
        } else {
          cleanupTextAreaScrollWatcher();
        }
      });

      const unwatchTemplateText = $scope.$watch(() => controller.templateTextContent, () => {
       $timeout(() => {
         if (pasteAreaScrollWatcher) {
           pasteAreaScrollWatcher();
         }
       });
      });

      $scope.$on('$destroy', () => {
        unwatchTextAreaActive();
        unwatchTemplateText();
        cleanupTextAreaScrollWatcher();
        controller.openFileBrowser = () => console.info('unbind openFileBrowser');
        controller.activateTextArea = () => console.info('unbind activateTextArea');
        if (sampleDataPopoverJQ) {
          sampleDataPopoverJQ.popover('destroy');
          sampleDataPopoverJQ = null;
        }
      });
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/components/project-import-modal/project-import-modal.ts